// FeeStructurePanel — fee heads per class (Tuition, Transport, Annual
// charges…), monthly or once a year, then one click applies the structure
// to every student in the class (respecting each student's concession).
import { useEffect, useState } from 'react';
import { Plus, Trash2, CheckCheck } from 'lucide-react';
import { getClasses, getFeeStructure, addFeeHead, deleteFeeHead, applyFeeStructure } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow, ClassSelect } from '../ui/Form';
import { inr, MONTH_NAMES } from '../ui/format';

const EMPTY = { head_name: '', amount: '', frequency: 'monthly', due_month: '4', scope: 'class', optional: false };

export default function FeeStructurePanel() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [heads,   setHeads]   = useState([]);
  const [all,     setAll]     = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [msg,     setMsg]     = useState('');
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    getClasses().then(cs => { setClasses(cs); if (cs.length) setClassId(String(cs[0].id)); }).catch(err => setMsg(errText(err)));
  }, []);
  useEffect(() => { if (classId) load(); }, [classId]);

  async function load() {
    try {
      const [scoped, everything] = await Promise.all([getFeeStructure(classId), getFeeStructure()]);
      setHeads(scoped);
      setAll(everything);
    } catch (err) { setMsg(errText(err)); }
  }

  async function add(e) {
    e.preventDefault();
    setMsg('');
    try {
      await addFeeHead({
        class_id: form.scope === 'class' ? Number(classId) : null,
        head_name: form.head_name, amount: Number(form.amount), frequency: form.frequency,
        due_month: form.frequency === 'one_time' ? Number(form.due_month) : undefined,
        optional: form.optional,
      });
      setForm({ ...EMPTY, frequency: form.frequency, scope: form.scope });
      load();
    } catch (err) { setMsg(errText(err)); }
  }

  async function remove(h) {
    const scopeText = h.class_id == null ? `"${h.head_name}" for ALL classes` : `"${h.head_name}"`;
    if (!window.confirm(`Remove ${scopeText}? Students keep what's already been charged until you apply again.`)) return;
    try { await deleteFeeHead(h.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  async function apply() {
    const cls = classes.find(c => String(c.id) === String(classId))?.name;
    if (!window.confirm(`Apply this structure to every student in ${cls}? Their monthly fee changes from now on; months already paid are not touched.`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await applyFeeStructure(Number(classId));
      setMsg(`Applied to ${r.students} student${r.students === 1 ? '' : 's'} in ${cls} ✅ · monthly fee ${inr(r.monthly_fee)} (before concessions)`);
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  const mandatoryHeads = heads.filter(h => !h.optional);
  const monthly = mandatoryHeads.filter(h => h.frequency === 'monthly').reduce((s, h) => s + h.amount, 0);
  const yearly = monthly * 12 + mandatoryHeads.filter(h => h.frequency === 'one_time').reduce((s, h) => s + h.amount, 0);
  const optionalCount = heads.filter(h => h.optional).length;
  const summary = classes.map(c => {
    const hs = all.filter(h => h.class_id === c.id || h.class_id == null);
    const mandatoryHs = hs.filter(h => !h.optional);
    const m = mandatoryHs.filter(h => h.frequency === 'monthly').reduce((s, h) => s + h.amount, 0);
    return { ...c, monthly: m, yearly: m * 12 + mandatoryHs.filter(h => h.frequency === 'one_time').reduce((s, h) => s + h.amount, 0), heads: hs.length };
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Fee Structure</h2>
        <ClassSelect classes={classes} value={classId} onChange={setClassId} />
      </div>
      <Msg msg={msg} className="mb-3" />

      <div className="grid grid-cols-[1fr_minmax(280px,360px)] gap-5 items-start max-[1000px]:grid-cols-1">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title={`Fee heads — Class ${classes.find(c => String(c.id) === String(classId))?.name || ''}`} action={
              <Button onClick={apply} disabled={!mandatoryHeads.length || busy} className="!py-1.5 !text-xs flex items-center gap-1.5">
                <CheckCheck size={13} /> {busy ? 'Applying…' : 'Apply to all students'}
              </Button>
            } />
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Fee head</th><th className="px-4 py-2.5">Charged</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {heads.map(h => (
                  <tr key={h.id} className="border-t border-line-soft">
                    <td className="px-4 py-3 font-semibold">
                      {h.head_name}{' '}
                      {h.class_id == null && <Badge tone="info">All classes</Badge>}{' '}
                      {h.optional && <Badge tone="caution">Optional</Badge>}
                    </td>
                    <td className="px-4 py-3">{h.frequency === 'monthly' ? 'Every month' : `Once, in ${MONTH_NAMES[h.due_month - 1]}`}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold">{inr(h.amount)}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => remove(h)} aria-label="Remove fee head" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {!heads.length && <EmptyRow cols={4}>No fee heads for this class yet</EmptyRow>}
              </tbody>
            </table>
            {heads.length > 0 && (
              <div className="flex justify-end gap-6 px-5 py-3 border-t border-line bg-paper rounded-b-m text-sm flex-wrap">
                <span>Mandatory monthly: <b className="tabular-nums">{inr(monthly)}</b></span>
                <span>Mandatory yearly total: <b className="tabular-nums">{inr(yearly)}</b></span>
                {optionalCount > 0 && <span className="text-ink-soft">{optionalCount} optional head{optionalCount === 1 ? '' : 's'} — opted in per student</span>}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="All classes" />
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Heads</th><th className="px-4 py-2.5 text-right">Monthly</th><th className="px-4 py-2.5 text-right">Yearly</th></tr></thead>
              <tbody>
                {summary.map(c => (
                  <tr key={c.id} className="border-t border-line-soft cursor-pointer hover:bg-transit-tint/40" onClick={() => setClassId(String(c.id))}>
                    <td className="px-4 py-2.5 font-semibold">{c.name}</td>
                    <td className="px-4 py-2.5">{c.heads || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{c.monthly ? inr(c.monthly) : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{c.yearly ? inr(c.yearly) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

        <Card className="p-5">
          <h3 className="font-display font-bold text-[15px] text-ink mb-3">Add fee head</h3>
          <form onSubmit={add} className="flex flex-col gap-3">
            <Field label="Name"><input required maxLength={60} className={inputCls} placeholder="e.g. Tuition fee" value={form.head_name} onChange={e => setForm({ ...form, head_name: e.target.value })} /></Field>
            <Field label="Amount (₹)"><input required type="number" min="1" step="any" className={inputCls} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Charged">
              <select className={inputCls} value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}>
                <option value="monthly">Every month</option>
                <option value="one_time">Once a year</option>
              </select>
            </Field>
            {form.frequency === 'one_time' && (
              <Field label="In which month">
                <select className={inputCls} value={form.due_month} onChange={e => setForm({ ...form, due_month: e.target.value })}>
                  {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
            )}
            <Field label="Applies to">
              <div className="flex gap-4 text-sm font-normal text-ink">
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="scope" checked={form.scope === 'class'} onChange={() => setForm({ ...form, scope: 'class' })} /> This class
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="scope" checked={form.scope === 'all'} onChange={() => setForm({ ...form, scope: 'all' })} /> All classes
                </label>
              </div>
            </Field>
            <label className="flex items-center gap-1.5 text-sm font-normal text-ink">
              <input type="checkbox" checked={form.optional} onChange={e => setForm({ ...form, optional: e.target.checked })} />
              Optional — students opt in individually
            </label>
            <Button type="submit" disabled={!classId} className="flex items-center gap-1.5 w-fit"><Plus size={14} /> Add</Button>
          </form>
          <p className="text-xs text-ink-soft mt-4">
            After adding mandatory heads, click <b>Apply to all students</b>. Optional heads (e.g. Transport) are opted in per student on their Fees tab instead. Concessions are set per student on their profile (Fees tab) and are applied automatically.
          </p>
        </Card>
      </div>
    </div>
  );
}
